import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  CreditSimulatorContent,
  CreditSimulatorHeader,
  CreditSimulatorWrapper,
} from './styles';
import { Tabs } from '../../../../../../app/components/Tabs';
import { Heading3 } from '../../../../../../app/components/typography';
import { TProducts } from 'modules/products/context/ProductsProvider/products.interfaces';
import { TQualification } from 'modules/customer/interfaces/qualifications';
import { TABS_CONSIGNED_CONTENT, TABS_CONTENT } from './constants';
import CreditSimulatorForm, {
  CreditSimulatorFormRefs,
} from './CreditSimulatorForm';
import {
  TSimulatorV2Request,
  TSimulatorV2Response,
} from 'app/services/simulatorHttpServiceV2';
import useCreditSimulatorV2 from 'app/hooks/useCreditSimulatorV2';
import { ValidateBilletsResponse } from 'modules/products/interfaces/billets';
import { ValidateEmvResponse } from 'modules/products/interfaces/qrCode';
import CreditSimulatorPreview, {
  CreditSimulatorPreviewRefs,
} from './CreditSimulatorPreview';
import CreditSimulatorSchedule from './CreditSimulatorSchedule';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { formatCurrency, formatToFixed } from 'app/utils/normalizer';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import { format } from 'date-fns';
import { ConsigneeOrganType } from '../../ConsignedStep/interfaces';
import { ApplicationRequestContext } from '../../ApplicationRequest';
import { TLoanGuaranteeFormValues } from '../../ConsultaFgts/interfaces';

export type CreditSimulatorRefs = {
  simulate: () => void;
  reset: () => void;
  copyPreview: () => void;
  validateGuarantees: () => Promise<boolean>;
  isValid: boolean;
  isLoading: boolean;
};
type CreditSimulatorProps = {
  product?: TProducts | null;
  qualification?: TQualification | null;
  simulationResponse?: TSimulatorV2Response | null;
  simulationRequest: TSimulatorV2Request | null;
  billetValidationResponse?: ValidateBilletsResponse | null;
  qrcodeEmvValidationResponse?: ValidateEmvResponse | null;
  onSimulate?: (
    simulation: TSimulatorV2Response,
    simulationRequest: TSimulatorV2Request,
    tabIndex: string,
    loanGuaranteeFormValues?: TLoanGuaranteeFormValues,
  ) => void;
  onGuaranteeFinancedValidation?: (
    formValues: TLoanGuaranteeFormValues,
    options?: { showErrors?: boolean },
  ) => void;
  onChangeFormMeta?: (isLoading: boolean, isValid: boolean) => void;
  hasTimeValidation?: boolean;
  onCTFirstPaymentDateChange?: (filled: boolean) => void;
};

const CreditSimulator = forwardRef<CreditSimulatorRefs, CreditSimulatorProps>(
  (
    {
      product,
      qualification,
      simulationResponse,
      simulationRequest,
      billetValidationResponse,
      qrcodeEmvValidationResponse,
      onSimulate,
      onGuaranteeFinancedValidation,
      onChangeFormMeta,
      hasTimeValidation = false,
      onCTFirstPaymentDateChange,
    },
    ref,
  ) => {
    // Hooks
    const formRef = useRef<CreditSimulatorFormRefs>(null);
    const previewRef = useRef<CreditSimulatorPreviewRefs>(null);
    const { showSnackbarCopy } = useSnackbar();
    const {
      simulate,
      simulationLoading,
      firstPaymentDateError,
      setFirstPaymentDateError,
    } = useCreditSimulatorV2();
    const { consignedProduct, fgtsPeriods, consignedList } = useContext(
      ApplicationRequestContext,
    );

    // States
    const [tabIndex, setTabIndex] = useState('0');
    const [simulationResponseData, setSimulationResponseData] =
      useState<TSimulatorV2Response | null>(simulationResponse ?? null);

    // Variables
    const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
    const isConsigned =
      !!product?.organ_type &&
      [ConsigneeOrganType.CT, ConsigneeOrganType.SIAPE].includes(
        product?.organ_type,
      );

    // Handlers
    const handleCopyPreview = () => {
      navigator.clipboard.writeText(
        `Quant. parcelas: ${
          simulationResponseData?.num_periods
            ? simulationResponseData?.num_periods
            : '-'
        }\nValor da parcela: ${
          simulationResponseData?.payment_amount
            ? formatCurrency(simulationResponseData?.payment_amount)
            : '-'
        }\nValor solicitado: ${
          simulationResponseData?.requested_amount
            ? formatCurrency(simulationResponseData?.requested_amount || 0.0)
            : '-'
        }\nValor financiado: ${
          simulationResponseData?.financed_amount
            ? formatCurrency(simulationResponseData?.financed_amount || 0.0)
            : '-'
        }\nJuros: ${
          simulationResponseData?.interest_rate
            ? `${formatToFixed(simulationResponseData?.interest_rate * 100, 2)
                .toString()
                .replace('.', ',')}% a.m.`
            : '-'
        }\nCET: ${
          typeof simulationResponseData?.monthly_effective_interest_rate ===
          'number'
            ? `${formatToFixed(
                simulationResponseData?.monthly_effective_interest_rate * 100,
                2,
              )
                .toString()
                .replace('.', ',')}% a.m.`
            : '-'
        }\nTC: ${
          simulationResponseData?.tac_amount
            ? formatCurrency(simulationResponseData?.tac_amount)
            : '-'
        }\nIOF: ${
          simulationResponseData?.iof_amount
            ? formatCurrency(simulationResponseData?.iof_amount)
            : '-'
        }\nOutros custos: ${
          simulationResponseData?.finance_fee
            ? formatCurrency(simulationResponseData?.finance_fee)
            : '-'
        }\nPrimeiro vencimento: ${
          simulationResponseData?.first_payment_date
            ? formatDateFromBackend(
                simulationResponseData?.first_payment_date,
              ).toLocaleDateString('pt-br')
            : '-'
        }\nÚltimo vencimento: ${
          simulationResponseData?.last_payment_date
            ? formatDateFromBackend(
                simulationResponseData?.last_payment_date,
              ).toLocaleDateString('pt-br')
            : '-'
        }\nSaldo Total: ${
          simulationResponseData?.total_amount_owed
            ? formatCurrency(simulationResponseData?.total_amount_owed)
            : '-'
        }`,
      );
      showSnackbarCopy();
    };

    useImperativeHandle(
      ref,
      () => ({
        simulate: () => {
          formRef.current?.simulate();
        },
        reset: () => {
          formRef.current?.reset();
        },
        copyPreview: handleCopyPreview,
        validateGuarantees: () =>
          formRef.current?.validateGuarantees() ?? Promise.resolve(true),
        isValid: !!previewRef.current?.isValid,
        isLoading: simulationLoading,
      }),
      [previewRef.current?.isValid, simulationLoading],
    );

    useEffect(() => {
      if (typeof onChangeFormMeta === 'function')
        onChangeFormMeta(simulationLoading, !!previewRef.current?.isValid);
    }, [simulationLoading, previewRef.current?.isValid]);

    const onFormSuccess = (data: TSimulatorV2Request) => {
      if (product) {
        const useNotificationOnly =
          product?.organ_type === ConsigneeOrganType.CT;
        simulate(
          product.id,
          {
            ...data,
            balanceCheckId: isFgts ? consignedList?.id : undefined,
            periods: isFgts ? fgtsPeriods : undefined,
            numPayments: isFgts ? fgtsPeriods?.length || 0 : data?.numPayments,
          },
          { useNotificationOnly },
        )
          .then((response) => {
            if (response) {
              setSimulationResponseData(response);
              if (typeof onSimulate === 'function')
                onSimulate(
                  response,
                  data,
                  tabIndex,
                  formRef.current?.getLoanGuaranteeFormValues(),
                );
            }
          })
          .catch((error) => {
            console.error('Erro ao simular:', error);
          });
      }
    };

    const simulateWithDifferentDisbursementDate = (date: string) => {
      if (product && simulationRequest) {
        const formattedDate = format(new Date(date), 'yyyy-MM-dd');
        const useNotificationOnly =
          product?.organ_type === ConsigneeOrganType.CT;

        simulate(
          product.id,
          {
            ...simulationRequest,
            disbursementDate: formattedDate,
            balanceCheckId: isFgts
              ? consignedProduct?.balance_check_id
              : undefined,
            periods: isFgts ? fgtsPeriods : undefined,
          },
          { useNotificationOnly },
        )
          .then((response) => {
            if (response) {
              setSimulationResponseData(response);
              // IMPORTANTE: Atualiza o contexto para salvar a data selecionada
              // Mantém o simulationRequest original (com data fim do desembolso) para preservar o range
              if (typeof onSimulate === 'function') {
                onSimulate(
                  response,
                  simulationRequest,
                  tabIndex,
                  formRef.current?.getLoanGuaranteeFormValues(),
                );
              }
            }
          })
          .catch((error) => {
            console.error('Erro ao simular com data diferente:', error);
          });
      }
    };

    const handleChangeTabIndex = (value: number) => {
      setTabIndex(String(value));
    };

    return (
      <CreditSimulatorWrapper>
        <CreditSimulatorHeader>
          <Heading3>Valores da solicitação</Heading3>
          {!isFgts && (
            <Tabs
              className="credit-simulator-tabs"
              content={isConsigned ? TABS_CONSIGNED_CONTENT : TABS_CONTENT}
              selectedIndex={Number(tabIndex)}
              style={{ fontSize: '16px' }}
              handleClick={handleChangeTabIndex}
            />
          )}
        </CreditSimulatorHeader>
        <CreditSimulatorContent>
          <CreditSimulatorForm
            ref={formRef}
            tabIndex={tabIndex}
            product={product}
            simulationRequest={simulationRequest}
            billetValidationResponse={billetValidationResponse}
            qrcodeEmvValidationResponse={qrcodeEmvValidationResponse}
            onFormSuccess={onFormSuccess}
            hasTimeValidation={hasTimeValidation}
            onGuaranteeFinancedValidation={onGuaranteeFinancedValidation}
            onCTFirstPaymentDateChange={onCTFirstPaymentDateChange}
            firstPaymentDateError={firstPaymentDateError}
            onClearFirstPaymentDateError={() => setFirstPaymentDateError(false)}
          />
          {!isFgts && (
            <CreditSimulatorSchedule
              product={product}
              simulationRequest={simulationRequest}
              simulationResponse={simulationResponseData}
              simulateWithDifferentDisbursementDate={
                simulateWithDifferentDisbursementDate
              }
            />
          )}
          <CreditSimulatorPreview
            ref={previewRef}
            simulationResponse={simulationResponseData}
            qualification={qualification}
            simulationPayload={simulationRequest}
          />
        </CreditSimulatorContent>
      </CreditSimulatorWrapper>
    );
  },
);

CreditSimulator.displayName = 'CreditSimulator';
export default CreditSimulator;
